/**
 * React #418 na cauda do <body> (T-HYD418B).
 *
 * Depois do `<main>`, o React hidrata como irmãos diretos do <body> os scripts
 * que ele mesmo renderiza: `SliderRuntime`, `ToggleEscapeRuntime`, o
 * `LiveControls` do framework (`script#__DECO_STATE` + o `<script type=module>`
 * do admin) e, por último, o entry do TanStack (`import("/assets/main-….js")`).
 * O entry também é hidratado: o `<Scripts />` o renderiza no cliente enquanto
 * `hydrated` é false (o manifest do root vai desidratado) e o remove depois.
 * Em modo draft, o `DraftPreviewIndicator` põe mais um script entre o
 * `ToggleEscapeRuntime` e o `__DECO_STATE`. Os terceiros do GTM (Insider,
 * ManyChat, Criteo, Bing, fbevents, OneTrust…) fazem `document.body.appendChild`
 * durante o parse, então os nós deles caem ENTRE o `</main>` e esses scripts.
 *
 * No <body> o React pula irmão de outra tag, mas qualquer `<script>` inline
 * casa com o `<script>` esperado: o hidratador "reivindica" os scripts do GTM
 * no lugar dos nossos e guarda como cursor o `nextSibling` do último nó
 * reivindicado. A hidratação roda em `startTransition` (fatiada); se no
 * intervalo o terceiro remove ou move o nó do cursor, o próximo irmão vira
 * `null` e o React lança #418 no `script` do `LiveControls`. Medido em
 * produção pelo `componentStack` do `onRecoverableError`:
 * `at script / at Mw (LiveControls) / at Dw / at body`. A recuperação é na
 * raiz: a página inteira é re-renderizada no cliente.
 *
 * A correção: antes do `hydrateRoot`, encostar os nossos scripts logo depois do
 * `<main>`, na ordem em que estão. Assim o cursor só passa por nós nossos, que
 * nenhum terceiro toca. Mover um `<script>` que já rodou não o executa de novo.
 *
 * Todo nó que o React renderiza depois do `<main>` tem de ser reconhecido por
 * `isReactTailNode` (no site, com `data-deco-tail`). Um nó que fica de fora é
 * ultrapassado pelos que se movem, e o React passa a procurá-lo fora do lugar.
 *
 * O planejamento é puro (sem DOM) para o teste rodar com `tsx`.
 */

export const TAIL_ATTR = "data-deco-tail";

export interface TailNode {
  nodeType: number;
  tagName?: string;
  id?: string;
  textContent?: string | null;
  hasAttribute?(name: string): boolean;
}

const isTag = (node: TailNode, tag: string) =>
  node.nodeType === 1 && node.tagName?.toUpperCase() === tag;

/** Nó que o React renderiza na cauda do <body> e precisa hidratar. */
export function isReactTailNode(node: TailNode): boolean {
  if (!isTag(node, "SCRIPT")) return false;
  if (node.hasAttribute?.(TAIL_ATTR)) return true;
  if (node.id === "__DECO_STATE") return true;
  const text = node.textContent ?? "";
  // O <script type="module"> do LiveControls (sem id nem atributo próprio).
  if (text.includes("__DECO_LIVE_CONTROLS__")) return true;
  // O script do `DraftPreviewIndicator` (só em modo draft).
  if (text.includes("__DECO_DRAFT_POINTER__")) return true;
  // O entry do TanStack: `<script type="module" async>import("…")</script>` no
  // build, `import { injectIntoGlobalHook } …` no dev. Sem ele na lista, o React
  // reivindica no lugar dele o primeiro script inline de terceiro depois do
  // `LiveControls` (e o apaga quando o `Scripts` some).
  return (
    node.hasAttribute?.("async") === true &&
    !node.hasAttribute("src") &&
    /^\s*import\b/.test(text)
  );
}

/**
 * Dados os filhos do <body>, devolve os nós a reposicionar logo depois do
 * `<main>`, na ordem atual, ou `[]` se já estão encostados.
 */
export function planBodyTail<T extends TailNode>(children: readonly T[]): T[] {
  const mainIdx = children.findIndex((n) => isTag(n, "MAIN"));
  if (mainIdx === -1) return [];
  const after = children.slice(mainIdx + 1).filter((n) => n.nodeType === 1);
  const ours = after.filter(isReactTailNode);
  if (ours.length === 0) return [];
  return ours.every((n, i) => after[i] === n) ? [] : ours;
}

/** Aplica no documento. Chamado em `src/client.tsx` antes do `hydrateRoot`. */
export function settleBodyTail(doc: Document = document): number {
  const body = doc.body;
  if (!body) return 0;
  const children = Array.from(body.childNodes) as unknown as Array<TailNode & ChildNode>;
  const move = planBodyTail(children);
  if (move.length === 0) return 0;
  children.find((n) => isTag(n, "MAIN"))?.after(...move);
  return move.length;
}
